import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Circle, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABEL = { letter: "Standard Package", full_petty: "Premium Package" };

const inputCls =
  "w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-xl px-4 py-3 text-base focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/30 transition";

const Step = ({ done, active, label }) => (
  <div className="flex items-center gap-3">
    {done ? (
      <CheckCircle2 size={22} className="text-[#2563EB] shrink-0" />
    ) : (
      <Circle size={22} className={active ? "text-[#111827]" : "text-[#d4d4d8]"} />
    )}
    <span
      className={`text-sm font-semibold ${done || active ? "text-[#111827]" : "text-[#a1a1aa]"}`}
    >
      {label}
    </span>
  </div>
);

export default function TrackCase() {
  const [email, setEmail] = useState("");
  const [reference, setReference] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const resend = async () => {
    if (!email.trim()) {
      setError("Enter your email first, then tap resend.");
      return;
    }
    setResending(true);
    try {
      const { data } = await axios.post(`${API}/cases/resend`, { email });
      toast.success(data.message);
    } catch (err) {
      toast.error("Could not resend right now. Try again in a minute.");
    } finally {
      setResending(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/cases/lookup`, {
        email,
        reference,
      });
      setResult(data);
    } catch (err) {
      setError(
        "We couldn't find a case with that email and reference. Check the reference from your confirmation email."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="track-page" className="pt-[68px] min-h-[80vh]">
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-14">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-4 mb-4">
            <span className="h-[3px] w-12 bg-[#2563EB]" />
            <span className="text-xs font-bold tracking-[0.3em] uppercase text-[#71717a]">
              Case Tracking
            </span>
          </div>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl tracking-tight mb-3">
            Where&rsquo;s my case?
          </h1>
          <p className="text-[#52525B] mb-8">
            Enter the email you used and the reference from your confirmation
            email.
          </p>

          <form
            onSubmit={submit}
            className="bg-white border border-[#E5E7EB] rounded-3xl p-6 sm:p-8 space-y-4 shadow-sm"
          >
            <div>
              <label className="block text-sm font-semibold mb-1.5">Email</label>
              <input
                data-testid="track-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                placeholder="jane@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">
                Case reference
              </label>
              <input
                data-testid="track-reference-input"
                required
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className={inputCls}
                placeholder="e.g. 037beede"
              />
            </div>
            {error && (
              <p data-testid="track-error" className="text-sm font-semibold text-red-600">
                {error}
              </p>
            )}
            <button
              data-testid="track-submit-button"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#111827] text-[#FFFFFF] font-bold py-3.5 rounded-full hover:bg-[#1F2937] transition-colors disabled:opacity-60"
            >
              <Search size={16} />
              {loading ? "Looking..." : "Find my case"}
            </button>
            <button
              data-testid="track-resend-button"
              type="button"
              disabled={resending}
              onClick={resend}
              className="w-full text-xs font-semibold text-[#71717a] hover:text-[#111827] py-1 transition-colors"
            >
              {resending
                ? "Sending..."
                : "Lost your reference? Enter your email and tap here to resend the confirmation"}
            </button>
          </form>

          {result && (
            <motion.div
              data-testid="track-result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 bg-white border-2 border-[#2563EB] rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_-20px_rgba(37,99,235,0.4)]"
            >
              <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
                <div>
                  <p className="font-display font-semibold text-2xl">
                    {result.dispute_type}
                  </p>
                  <p className="text-sm text-[#71717a]">
                    Ref {result.reference} · {result.amount} ·{" "}
                    {PLAN_LABEL[result.plan] || result.plan}
                  </p>
                </div>
                <span
                  data-testid="track-payment-badge"
                  className={`text-xs font-bold px-3 py-1.5 rounded-full border ${
                    result.payment_status === "paid"
                      ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                      : "bg-[#F3F4F6] text-[#71717a] border-[#E5E7EB]"
                  }`}
                >
                  {result.payment_status === "paid" ? "Paid" : "Unpaid"}
                </span>
              </div>
              <div className="space-y-3.5 border-t border-[#E5E7EB] pt-5">
                <Step done label="Case received" />
                <Step
                  done={result.payment_status === "paid"}
                  active={result.payment_status !== "paid"}
                  label="Payment received"
                />
                <Step
                  done={result.status === "handled"}
                  active={result.status !== "handled"}
                  label="Handled by a Petty Agent"
                />
              </div>
              <p className="text-xs text-[#a1a1aa] mt-5">
                Submitted {new Date(result.created_at).toLocaleDateString()}.
                Questions? Reply to your confirmation email.
              </p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </main>
  );
}
